import { sourceColors, segmentColors, type Lead } from '../../../mocks/leads';

interface ApplicantDetailProps {
  lead: Lead;
  onBack: () => void;
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-white/30 text-[11px] font-semibold uppercase tracking-widest">{label}</span>
      <span className={`text-white/80 text-sm ${mono ? 'font-mono text-xs' : 'font-medium'}`}>{value}</span>
    </div>
  );
}

export default function ApplicantDetail({ lead, onBack }: ApplicantDetailProps) {
  const srcColor = sourceColors[lead.kaynak] || '#6b7280';
  const segColor = segmentColors[lead.segment_durum] || '#6b7280';

  function openWhatsApp() {
    const phone = lead.telefon.replace(/\D/g, '');
    const msg = encodeURIComponent(`Merhaba ${lead.ad_soyad}, franchise lansmanımıza davet etmek istiyoruz. Detaylar için hazırız!`);
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-white/40 hover:text-white transition-all cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-white">{lead.ad_soyad}</h1>
          <p className="text-white/30 text-xs font-mono mt-0.5">{lead.id}</p>
        </div>
        <div className="ml-auto flex gap-2">
          <button
            onClick={openWhatsApp}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#25d366]/20 hover:bg-[#25d366]/30 text-[#25d366] text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-whatsapp-line" />
            WhatsApp Aç
          </button>
          <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap">
            <i className="ri-mail-send-line" />
            Davet Gönder
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Info Card */}
        <div className="lg:col-span-2 rounded-xl bg-white/[0.03] border border-white/[0.07] p-6 space-y-6">
          <div>
            <p className="text-white/40 text-xs font-semibold uppercase tracking-widest mb-4">Temel Bilgiler</p>
            <div className="grid grid-cols-2 gap-5">
              <DetailRow label="Ad Soyad" value={lead.ad_soyad} />
              <DetailRow label="Telefon" value={lead.telefon} mono />
              <DetailRow label="İş Seviyesi" value={lead.is_seviyesi} />
              <DetailRow label="Oluşturma Tarihi" value={new Date(lead.olusturma_tarihi).toLocaleString('tr-TR')} />
            </div>
          </div>
          <div className="border-t border-white/[0.06] pt-5">
            <p className="text-white/40 text-xs font-semibold uppercase tracking-widest mb-4">Teknik Bilgiler</p>
            <div className="grid grid-cols-2 gap-5">
              <DetailRow label="IP Adresi" value={lead.ip_adresi} mono />
              <DetailRow label="User Agent" value={lead.user_agent} mono />
              {lead.manychat_user_id && <DetailRow label="ManyChat User ID" value={lead.manychat_user_id} mono />}
            </div>
          </div>
        </div>

        {/* Status Card */}
        <div className="space-y-4">
          <div className="rounded-xl bg-white/[0.03] border border-white/[0.07] p-5 space-y-4">
            <p className="text-white/40 text-xs font-semibold uppercase tracking-widest">Durum</p>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-white/50 text-xs">Kaynak</span>
                <span
                  className="text-xs font-bold px-3 py-1 rounded-full"
                  style={{ background: srcColor + '22', color: srcColor }}
                >
                  {lead.kaynak}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/50 text-xs">Segment</span>
                <span
                  className="text-xs font-bold px-3 py-1 rounded-full"
                  style={{ background: segColor + '22', color: segColor }}
                >
                  {lead.segment_durum}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/50 text-xs">Lansmann Katılım</span>
                <span
                  className={`text-xs font-bold px-3 py-1 rounded-full ${
                    lead.lansmanKatilim ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/[0.06] text-white/30'
                  }`}
                >
                  {lead.lansmanKatilim ? 'Evet' : 'Hayır'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/50 text-xs">Davet Gönderildi</span>
                <span
                  className={`text-xs font-bold px-3 py-1 rounded-full ${
                    lead.davetGonderildi ? 'bg-amber-500/20 text-amber-400' : 'bg-white/[0.06] text-white/30'
                  }`}
                >
                  {lead.davetGonderildi ? 'Gönderildi' : 'Gönderilmedi'}
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-white/[0.03] border border-white/[0.07] p-5">
            <p className="text-white/40 text-xs font-semibold uppercase tracking-widest mb-3">Hızlı İşlemler</p>
            <div className="space-y-2">
              <button
                onClick={openWhatsApp}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-[#25d366]/10 hover:bg-[#25d366]/20 text-[#25d366] text-sm font-medium transition-all cursor-pointer"
              >
                <i className="ri-whatsapp-line" />
                WhatsApp\'ta Aç
              </button>
              <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 text-sm font-medium transition-all cursor-pointer">
                <i className="ri-mail-send-line" />
                Lansman Daveti Gönder
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
